import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import logger from 'src/logger';
import { AuthManager } from '../../services/auth/authManager';
import { AUTH_CONFIG } from '../../services/auth/config';
import { AuthUser } from '../../services/auth/types';

interface UserContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  refreshToken: () => Promise<string | null>;
  signInWithProvider: (providerName: string) => Promise<void>;
  error: Error | null;
}

const UserContext = createContext<UserContextType | null>(null);

const MAX_REFRESH_RETRIES = 3;
const REFRESH_RETRY_DELAY = 5000; // 5 seconds

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [authManager] = useState(() => new AuthManager(AUTH_CONFIG));
  const auth = getAuth();
  
  // Use refs to track mounting state and retry counts
  const isMounted = useRef(true);
  const refreshRetries = useRef(0);
  const tokenRefreshInterval = useRef<NodeJS.Timeout>();

  const signInWithProvider = async (providerName: string) => {
    try {
      const provider = authManager.getProvider(providerName);
      if (!provider) {
        throw new Error(`Authentication provider '${providerName}' is not available. Please check your configuration.`);
      }

      const authUser = await provider.signIn();
      if (authUser) {
        setUser(authUser);
        setError(null);
      }
    } catch (error) {
      logger.error(`Error signing in with ${providerName}:`, error);
      setError(error instanceof Error ? error : new Error('Sign-in failed'));
      throw error;
    }
  };

  const refreshToken = async () => {
    if (!auth.currentUser || refreshRetries.current >= MAX_REFRESH_RETRIES) {
      return null;
    }

    try {
      const newToken = await auth.currentUser.getIdToken(true);
      if (isMounted.current) {
        setUser(prev => prev ? ({
          ...prev,
          accessToken: newToken
        }) : null);
        refreshRetries.current = 0; // Reset retry counter on success
        setError(null);
      }
      return newToken;
    } catch (error) {
      logger.error('Error refreshing token:', error);
      refreshRetries.current++;
      setError(error as Error);
      
      // Implement exponential backoff for retries
      if (refreshRetries.current < MAX_REFRESH_RETRIES) {
        await new Promise(resolve => 
          setTimeout(resolve, REFRESH_RETRY_DELAY * Math.pow(2, refreshRetries.current - 1))
        );
        return refreshToken(); // Retry
      }
      return null;
    }
  };

  const setupTokenRefresh = async () => {
    if (!auth.currentUser || !isMounted.current) return;

    try {
      const decodedToken = await auth.currentUser.getIdTokenResult();
      const expirationTime = new Date(decodedToken.expirationTime).getTime();
      const now = Date.now();
      const timeToExpiry = expirationTime - now;
      
      // Ensure we don't set up refresh if token is already expired
      if (timeToExpiry <= 0) {
        await refreshToken();
        return;
      }

      // Set up refresh 5 minutes before expiry
      const refreshTime = Math.max(timeToExpiry - 5 * 60 * 1000, 0);
      
      // Clear any existing interval
      if (tokenRefreshInterval.current) {
        clearTimeout(tokenRefreshInterval.current);
      }

      // Set up next refresh
      tokenRefreshInterval.current = setTimeout(async () => {
        if (isMounted.current) {
          await refreshToken();
          setupTokenRefresh(); // Set up next refresh only if previous succeeded
        }
      }, refreshTime);
    } catch (error) {
      logger.error('Error setting up token refresh:', error);
      setError(error as Error);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    refreshRetries.current = 0;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted.current) return;

      try {
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken();
          // Determine provider from Firebase user data
          const provider = firebaseUser.providerData?.[0]?.providerId?.replace('.com', '') || 'unknown';
          
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            accessToken: token,
            provider
          });
          await setupTokenRefresh();
        } else {
          setUser(null);
        }
      } catch (error) {
        logger.error('Auth state change error:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      isMounted.current = false;
      unsubscribe();
      if (tokenRefreshInterval.current) {
        clearTimeout(tokenRefreshInterval.current);
      }
    };
  }, [auth]);

  return (
    <UserContext.Provider value={{ user, setUser, refreshToken, signInWithProvider, error }}>
      {!isLoading ? children : <div>Loading...</div>}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};